<template>
  <v-row justify="center">
    <v-dialog
      v-model="open"
      width="600px"
      :fullscreen="isMobileBreakpoint"
      :persistent="isMobileBreakpoint"
    >
      <v-card>
        <v-toolbar elevation="0" color="background">
          <v-toolbar-title class="renameVideoModalTitle ml-3">
            {{ $t('message.pages.organization.cancelModal.wantProceed') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="handleClose()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="pl-7 pr-5 py-0 renameVideoModalDescription">
          <v-container class="pl-0 pt-1">
            <v-row no-gutters>
              <v-col cols="12" class="instructions">
                <p class="body-bold-base mb-1">
                  {{
                    $t(
                      'message.pages.organization.cancelModal.fallowStepsToCancel'
                    )
                  }}
                </p>
                <ol>
                  <li class="body-regular-small">
                    {{ $t('message.pages.organization.cancelModal.firstStep') }}
                  </li>
                  <li class="body-regular-small">
                    {{
                      $t('message.pages.organization.cancelModal.secondStep')
                    }}
                  </li>
                  <li class="body-regular-small">
                    {{ $t('message.pages.organization.cancelModal.thirdStep') }}
                  </li>
                </ol>
              </v-col>
              <v-col cols="12" class="mb-3 mt-4">
                <BaseAlert
                  type="warning"
                  :title="
                    $t('message.pages.organization.cancelModal.adviceTitle')
                  "
                  :description="
                    $t(
                      'message.pages.organization.cancelModal.adviceSubscription'
                    )
                  "
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions
          class="px-8 py-6"
          :class="isMobileBreakpoint ? 'mobile-actions' : ''"
        >
          <v-spacer></v-spacer>
          <base-btn
            type="tertiary"
            text
            class="mr-4 px-4"
            :disabled="isFetching"
            @click="handleClose"
          >
            {{ $t('message.pages.organization.cancelModal.exit') }}
          </base-btn>

          <base-btn
            type="primary"
            large
            min-width="185px"
            @click="handleProceed"
            :loading="isFetching"
          >
            {{ $t('message.pages.organization.cancelModal.openChat') }}
          </base-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import BaseAlert from '@/components/BaseAlert.vue'
import planApi from '@/services/planApi'
import invoiceApi from '@/services/invoiceApi'
import i18n from '@/translations/lang'
import calcMegabytesMixin from '@/utils/calcMegabytes.js'
import { mapState } from 'vuex'

export default {
  name: 'CancelSubscriptionModal',
  components: { BaseAlert },
  mixins: [calcMegabytesMixin],
  data: () => ({
    open: false,
    isFetching: false,
    video: {
      name: '',
    },
    edit: false,
    videoId: '',
    valid: false,
  }),
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
      userPlanInfo: 'userPlanInfo',
    }),
  },
  mounted() {
    if (!window.db) this.$root.$emit('initDexie')
    this.$root.$on('cancelSubscriptionModal', () => {
      this.open = true
    })
  },
  beforeDestroy() {
    this.$root.$off('cancelSubscriptionModal')
    this.$root.$emit('resetIsAnyOtherModal')
    this.$root.$emit('cancelSubscriptionModalHasClosed')
  },
  methods: {
    ...mapActions('video', ['getVideoConfig', 'getVideoProfileId']),
    ...mapMutations('video', [
      'UPDATE_VIDEO',
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_PLAYER',
      'UPDATE_VIDEO_PROFILE_ID',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async fetchInvoices() {
      try {
        let ret = await invoiceApi.getInvoices()
        const invoices = JSON.parse(JSON.stringify(ret.data.invoices))
        const totalAmountPaid = invoices.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.amount_paid
        }, 0)
        return (totalAmountPaid / 100).toFixed(2)
      } catch (e) {
        console.log('e', e)
      }
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      if (this.$i18next.resolvedLanguage === 'es') return 'es'
      return 'pt-BR'
    },
    async getThisCycleInfoWithProrate() {
      let items = []
      try {
        let ret = await planApi.previewInvoice(
          this.userPlanInfo.subscription_id,
          this.getLocale().toLowerCase(),
          items
        )
        return ret.data.amount_due / 100
      } catch (error) {
        console.error('error', error)
      }
    },
    async formatPlanInfo(userInfo, planInfo) {
      let totalLength = 0
      if (window.db?.videos) {
        try {
          totalLength = await window.db.videos.count()
        } catch (err) {
          console.error(err)
        }
      }
      const [invoiceValue, userPaidValue] = await Promise.all([
        this.getThisCycleInfoWithProrate(),
        this.fetchInvoices(),
      ])

      let usedTraffic = String(
        this.calcMegabytes(planInfo?.limit?.traffic, 'MB', 'GB', true)
      ).replace(',', '.')
      const planValue = (planInfo.subscription.unit_amount / 100).toFixed(2)
      const nowValue = this.formatDate(Date.now())

      let infoArray = [
        ['Atualizado_em', `${nowValue}`],
        ['Data_pedido_cancelamento', `${nowValue}`],
        ['Nome', `${userInfo.name}`],
        ['Email', `${userInfo.email}`],
        ['Telefone', `${userInfo.phone}`],
        ['Preco_plano_atual', `${planValue}`],
        [
          'Periodo_atual',
          `${this.formatDate(
            planInfo.limit.start_period
          )} até ${this.formatDate(planInfo.limit.end_period)}`,
        ],
        ['Status_assinatura', `${planInfo.subscription.status}`],
        ['Criado_em', `${this.formatDate(userInfo.created_at)}`],
        ['Banda_utilizada', `${usedTraffic}`],
        [
          'Videos_plataforma',
          `${totalLength > 0 ? totalLength : 'Não identificado'}`,
        ],
        ['Valor_invoice_atual', `${invoiceValue ?? 'Não identificado'}`],
        ['Valor_pago_acumulado', `${userPaidValue ?? 'Não identificado'}`],
        ['currency', `${planInfo.currency ?? 'Não identificado'}`],
        [
          'Cargo',
          `${planInfo.customer_metadata.client_role ?? 'Não identificado'}`,
        ],
        [
          'Tamanho_da_equipe',
          `${
            planInfo.customer_metadata.client_team_size ?? 'Não identificado'
          }`,
        ],
        [
          'Razao_da_conta',
          `${
            planInfo.customer_metadata.client_reasoning ?? 'Não identificado'
          }`,
        ],
      ]

      return infoArray
    },
    formatDate(timestamp) {
      const date = new Date(timestamp)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}/${month}/${year} - ${hours}h${minutes}`
    },
    async handleProceed() {
      this.isFetching = true
      try {
        if (typeof this.$crisp !== 'undefined') {
          let planInfoText = await this.formatPlanInfo(
            this.$store.state.user.userAllInfo,
            this.$store.state.user.userPlanInfo
          )
          this.$crisp.push(['do', 'chat:show'])
          this.$crisp.push(['do', 'chat:open'])
          this.$crisp.push([
            'on',
            'chat:opened',
            this.$crisp.push(['set', 'session:data', [planInfoText]]),
            this.$crisp.push([
              'do',
              'message:send',
              ['text', i18n.t('message.pages.organization.cancel_info')],
            ]),
          ])
        }
      } catch (err) {
        this.isFetching = false
      }
      this.isFetching = false
      this.handleClose()
    },
    handleClose() {
      this.open = false
      this.$root.$emit('cancelSubscriptionModalHasClosed')
    },
  },
}
</script>

<style lang="scss" scoped>
.instructions {
  color: varFigma(neutral-30-base);
}
.renameVideoModalTitle {
  color: varFigma(neutral-20-dark);
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 21.6px */
}
.v-toolbar__title {
  white-space: normal;
}
.mobile-actions {
  position: fixed;
  bottom: 0;
  left: -46px;
  width: 100%;
  background-color: var(--v-background-base);
  z-index: 10;
  padding: 16px;
}
</style>
